/**
 * 应用状态容器（M2-03 / DESIGN.md §4.1 应用层）。
 *
 * 把 M1 造好的零件装配成一台机器：TileStore（像素）、CommandBus（修改）、
 * History（撤销）在这里聚合，UI 只订阅「变了」这一个事实，具体读什么各取所需。
 *
 * 修改路径依然只有一条：{@link AppStore.dispatch} → CommandBus →（订阅回调）
 * → History。undo/redo 走历史系统的换引用路径。UI 拿到的 document 是只读的。
 */
import type { Command } from './command-bus';
import { CommandBus } from './command-bus';
import type { CommandSource } from './actions/action-recorder';
import { History, PixelTransaction, type PixelDiff } from './history';
import type { Adjustment, Document, GroupLayer } from '../document/types';
import { findLayer } from '../document/draft';
import type { LayerId } from '../engine/tile/key';
import { TileStore } from '../engine/tile/store';
import type { SelectionMask } from '../engine/selection/mask';
import type { AppState } from './actions/registry';
import type { RecoverableEntry } from '../io/gateway';

export interface AppStoreOptions {
  /** 时间源。注入以便测试确定。 */
  readonly now?: () => string;
}

/** sRGB 字节色（0–255）。画笔/填充/吸管共用。 */
export interface RgbColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

function clampChannel(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

export class AppStore {
  /** 像素的唯一归属（CLAUDE.md 规则 6）。 */
  readonly tiles = new TileStore();

  readonly #now: () => string;
  #bus: CommandBus | null = null;
  #history: History | null = null;
  #document: Document | null = null;
  #hint = '';
  #recoverable: readonly RecoverableEntry[] = [];
  /** 当前选区。null 表示没有选区（与「全选」不同）。 */
  #selection: SelectionMask | null = null;
  /** dispatchWithPixels 的携带槽（M3-11）：订阅回调把它并进同一条历史。 */
  #pendingPixels: PixelDiff | null = null;
  /** 调整参数的拖动预览（M3-06）：不进历史的显示态覆盖。 */
  #adjustmentPreview: { readonly id: LayerId; readonly adjustment: Adjustment } | null = null;
  #previewDocument: Document | null = null;
  #previewBase: Document | null = null;
  /** 前景/背景色（M3-04）。默认黑前景/白背景，与 PS 一致。 */
  #foreground: RgbColor = { r: 0, g: 0, b: 0 };
  #background: RgbColor = { r: 255, g: 255, b: 255 };
  /** 活动图层（M3-01）。null 仅在无文档时。 */
  #activeLayerId: LayerId | null = null;
  /**
   * 图层面板的多选（右键"合并图层"/多图层编组要用，DESIGN.md §7.1 补充）。
   * 纯 UI 态，不接 Command Bus、不需要撤销——跟 `#activeLayerId` 同一类东西，
   * 只是允许多个。`setActiveLayer` 单选时会把这个集合收敛成只含那一个 id，
   * 保持"活动图层永远在选中集合里"这条不变式。
   */
  #selectedLayerIds: ReadonlySet<LayerId> = new Set();
  /** 活动图层最后一次确认时的位置（父组 id + 下标），删除后回退相邻用。 */
  #activeParentId: LayerId | null = null;
  #activeIndex = 0;
  #pixelEpoch = 0;
  #pixelDirty: ReadonlyArray<{ readonly tx: number; readonly ty: number }> = [];
  readonly #listeners = new Set<() => void>();

  constructor(options: AppStoreOptions = {}) {
    this.#now = options.now ?? (() => new Date().toISOString());
  }

  /* ---------------- 订阅 ---------------- */

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify(): void {
    for (const listener of this.#listeners) listener();
  }

  /* ---------------- 文档生命周期 ---------------- */

  get document(): Document | null {
    return this.#document;
  }

  get history(): History | null {
    return this.#history;
  }

  /**
   * 装入一份新文档（新建/打开/恢复都走这里）。
   * 历史清零：跨文档的撤销没有意义，还会把上一份文档的像素快照钉在内存里。
   */
  setDocument(document: Document): void {
    this.#document = document;
    this.#selection = null; // 选区不跨文档
    // 活动图层默认最上层 raster（M3-01）
    this.#activeLayerId = topmostRaster(document.root);
    this.#selectedLayerIds = this.#activeLayerId === null ? new Set() : new Set([this.#activeLayerId]);
    this.#rememberActivePath();

    this.#history = new History(document, { store: this.tiles });
    const bus = new CommandBus({ document, now: this.#now });
    bus.subscribe((result) => {
      if (result.changed) {
        this.#history?.push({
          label: result.command.label,
          inverse: result.inverse,
          document: result.document,
          structuralCost: result.command.cost,
          pixels: this.#pendingPixels,
        });
      }
      this.#document = result.document;
      this.#reconcileActive();
      this.#notify();
    });
    this.#bus = bus;
    this.#notify();
  }

  /* ---------------- 修改的唯一入口 ---------------- */

  dispatch(command: Command): void {
    if (this.#bus === null) throw new Error('没有打开的文档，不能派发命令。');
    this.#bus.dispatch(command);
  }

  /**
   * 只读订阅入口，供动作录制器（M4-04 / action-recorder.ts）观察命令流。
   * 返回类型只声明 `subscribe`——修改文档必须仍然只走 {@link dispatch}，
   * 不能靠这个访问器绕过 store 的历史/像素记账偷偷拿到 `CommandBus.dispatch`。
   */
  get commandBus(): CommandSource | null {
    return this.#bus;
  }

  /**
   * 结构命令 + 像素差分合成**一条**历史（M3-11，裁剪/蒙版反相这类复合操作）。
   * undo 一步同时还原结构与像素。diff 的事务必须已经 end()。
   */
  dispatchWithPixels(command: Command, diff: PixelDiff | null): void {
    this.#pendingPixels = diff;
    try {
      this.dispatch(command);
    } finally {
      this.#pendingPixels = null;
    }
  }

  undo(): void {
    const history = this.#history;
    if (history === null) return;
    // undo 前记下条目：像素条目要把受影响的块报给渲染桥
    const entry = history.entries[history.cursor];
    const document = history.undo();
    if (document === null) return;
    this.#bus?.replaceDocument(document);
    this.#document = document;
    this.#reconcileActive();
    if (entry?.pixels != null) {
      this.invalidatePixels(entry.pixels.keys.map((k) => ({ tx: k.tx, ty: k.ty })));
    } else {
      this.#notify();
    }
  }

  redo(): void {
    const history = this.#history;
    if (history === null) return;
    // redo 会前移游标：先记下将要重做的条目，供像素失效通知
    const entry = history.entries[history.cursor + 1];
    const document = history.redo();
    if (document === null) return;
    this.#bus?.replaceDocument(document);
    this.#document = document;
    this.#reconcileActive();
    if (entry?.pixels != null) {
      this.invalidatePixels(entry.pixels.keys.map((k) => ({ tx: k.tx, ty: k.ty })));
    } else {
      this.#notify();
    }
  }

  /**
   * 删除某条历史（含它自己）及其后所有条目——历史面板垃圾桶按钮用，
   * 跟 undo()/redo() 一样要维护 `#document`/`#bus`/活动图层/渲染桥像素失效
   * 通知，区别是 `History.deleteFrom` 内部可能一次性退好几步（游标当前
   * 落在待删范围内时），得把沿途经过的每一条像素条目的块坐标都收集起来，
   * 不能只报最后一步。
   */
  deleteHistoryEntry(index: number): void {
    const history = this.#history;
    if (history === null) return;
    const startCursor = history.cursor;
    const keys: Array<{ tx: number; ty: number }> = [];
    for (let i = startCursor; i >= index; i--) {
      const entry = history.entries[i];
      if (entry?.pixels != null) keys.push(...entry.pixels.keys.map((k) => ({ tx: k.tx, ty: k.ty })));
    }
    const document = history.deleteFrom(index);
    if (document === null) return;
    this.#bus?.replaceDocument(document);
    this.#document = document;
    this.#reconcileActive();
    if (keys.length > 0) {
      this.invalidatePixels(keys);
    } else {
      this.#notify();
    }
  }

  /* ---------------- 状态快照 ---------------- */

  state(): AppState {
    return {
      hasDocument: this.#document !== null,
      canUndo: this.#history?.canUndo ?? false,
      canRedo: this.#history?.canRedo ?? false,
      hasSelection: this.#selection !== null && !this.#selection.isEmpty,
      hasRecoverable: this.#recoverable.length > 0,
      hasActiveLayer: this.#activeLayerId !== null,
    };
  }

  /* ---------------- 像素操作（画笔等） ---------------- */

  /** 开启一段像素事务（一次笔画）。touch→写入→end 的纪律见 M1-08。 */
  beginPixelTransaction(label: string): PixelTransaction {
    return new PixelTransaction(this.tiles, label);
  }

  /**
   * 把一段像素差分记为**一条**历史（一笔 = 一条，§4.5）。
   * 文档结构不变，历史持有 COW 快照；redo 走 undo 时惰性捕获的
   * after 快照（M3-07 的双向差分），逐字节恢复。
   */
  commitPixels(label: string, diff: PixelDiff | null): void {
    if (diff === null || this.#document === null) return;
    this.#history?.push({ label, inverse: null, document: this.#document, pixels: diff });
    this.#notify();
  }

  /**
   * 像素内容失效通知（文档空间块坐标）。渲染桥据此做精确的按块重合成，
   * 与「结构变化=整体标脏」的粗粒度路径互补。
   */
  invalidatePixels(coords: ReadonlyArray<{ readonly tx: number; readonly ty: number }>): void {
    this.#pixelDirty = coords;
    this.#pixelEpoch++;
    this.#notify();
  }

  get pixelEpoch(): number {
    return this.#pixelEpoch;
  }

  get pixelDirty(): ReadonlyArray<{ readonly tx: number; readonly ty: number }> {
    return this.#pixelDirty;
  }

  /* ---------------- 调整预览（M3-06） ---------------- */

  /**
   * 设置/清除调整参数的拖动预览。预览**不修改文档也不进历史**——
   * 它只影响 {@link displayDocument} 的返回；松手时调用方派发真实命令。
   */
  setAdjustmentPreview(id: LayerId, adjustment: Adjustment | null): void {
    this.#adjustmentPreview = adjustment === null ? null : { id, adjustment };
    this.#previewDocument = null;
    this.#notify();
  }

  /**
   * 显示路径应读的文档：有调整预览时返回参数被覆盖的副本（memo 化，
   * 引用稳定到下一次预览/文档变化），否则就是 {@link document}。
   * 导出/保存等持久化路径仍必须读 {@link document}。
   */
  displayDocument(): Document | null {
    const document = this.#document;
    const preview = this.#adjustmentPreview;
    if (document === null || preview === null) return document;
    if (this.#previewDocument === null || this.#previewBase !== document) {
      this.#previewBase = document;
      this.#previewDocument = {
        ...document,
        root: overrideAdjustment(document.root, preview.id, preview.adjustment),
      };
    }
    return this.#previewDocument;
  }

  /* ---------------- 前景/背景色（M3-04） ---------------- */

  get foreground(): RgbColor {
    return this.#foreground;
  }

  get background(): RgbColor {
    return this.#background;
  }

  setForeground(color: RgbColor): void {
    this.#foreground = { r: clampChannel(color.r), g: clampChannel(color.g), b: clampChannel(color.b) };
    this.#notify();
  }

  setBackground(color: RgbColor): void {
    this.#background = { r: clampChannel(color.r), g: clampChannel(color.g), b: clampChannel(color.b) };
    this.#notify();
  }

  /** X 键：交换前景/背景色。 */
  swapColors(): void {
    [this.#foreground, this.#background] = [this.#background, this.#foreground];
    this.#notify();
  }

  /* ---------------- 活动图层（M3-01） ---------------- */

  get activeLayerId(): LayerId | null {
    return this.#activeLayerId;
  }

  /** 切换活动图层（单选）。id 不在当前文档树里时抛 RangeError，状态不变。 */
  setActiveLayer(id: LayerId): void {
    const document = this.#document;
    if (document === null) throw new Error('没有打开的文档，不能设置活动图层。');
    if (findLayer(document.root, id) === null) {
      throw new RangeError(`图层「${id}」不在当前文档里。`);
    }
    this.#activeLayerId = id;
    this.#selectedLayerIds = new Set([id]); // 单选收敛多选集合
    this.#rememberActivePath();
    this.#notify();
  }

  /** 当前多选的图层 id 集合。空集合与"只选中活动图层"是两种不同状态。 */
  get selectedLayerIds(): ReadonlySet<LayerId> {
    return this.#selectedLayerIds;
  }

  /**
   * 图层面板的 shift 范围多选 / cmd 追加多选提交这份集合（未知 id 会被
   * 静默忽略——面板自己算下标范围，不该因为文档同时被别处改动而抛错）。
   * 非空时把活动图层顺带设成集合里最后一个，保持"活动图层永远在选中
   * 集合里"这条不变式，混合模式下拉/右键菜单才知道该对谁生效。
   */
  setSelectedLayerIds(ids: readonly LayerId[]): void {
    const document = this.#document;
    if (document === null) return;
    const valid = ids.filter((id) => findLayer(document.root, id) !== null);
    this.#selectedLayerIds = new Set(valid);
    const last = valid[valid.length - 1];
    if (last !== undefined) {
      this.#activeLayerId = last;
      this.#rememberActivePath();
    }
    this.#notify();
  }

  /**
   * 绘制类工具的目标图层：活动图层是 raster 就用它，
   * 否则回退最上层 raster（活动图层可能是调整/文本层，不能直接落像素）。
   */
  activeRasterLayerId(): LayerId | null {
    const document = this.#document;
    if (document === null) return null;
    const active =
      this.#activeLayerId === null ? null : findLayer(document.root, this.#activeLayerId);
    if (active !== null && active.layer.kind === 'raster') return active.layer.id;
    return topmostRaster(document.root);
  }

  /** 记录活动图层当前的位置，供它被删后回退相邻图层。 */
  #rememberActivePath(): void {
    const document = this.#document;
    if (document === null || this.#activeLayerId === null) return;
    const location = findLayer(document.root, this.#activeLayerId);
    if (location === null) return;
    const parent = location.ancestors[location.ancestors.length - 1];
    this.#activeParentId = parent?.id ?? null;
    this.#activeIndex = location.index;
  }

  /**
   * 文档换引用后校正活动态：id 还在就只刷新位置；
   * 没了就回退到原父组同下标的相邻图层，父组也没了回退最上层 raster。
   */
  #reconcileActive(): void {
    const document = this.#document;
    if (document === null) {
      this.#activeLayerId = null;
      this.#selectedLayerIds = new Set();
      return;
    }
    if (this.#activeLayerId !== null && findLayer(document.root, this.#activeLayerId) !== null) {
      this.#rememberActivePath();
      this.#pruneSelectedLayerIds(document);
      return;
    }
    const parent =
      this.#activeParentId === null
        ? document.root
        : (findLayer(document.root, this.#activeParentId)?.layer ?? null);
    if (parent !== null && parent.kind === 'group' && parent.children.length > 0) {
      const index = Math.min(this.#activeIndex, parent.children.length - 1);
      this.#activeLayerId = parent.children[index]!.id;
    } else {
      this.#activeLayerId = topmostRaster(document.root);
    }
    this.#rememberActivePath();
    this.#pruneSelectedLayerIds(document);
  }

  /** 丢弃多选集合里已经不存在的 id（图层被别处删除/undo 之后）。 */
  #pruneSelectedLayerIds(document: Document): void {
    const pruned = [...this.#selectedLayerIds].filter((id) => findLayer(document.root, id) !== null);
    this.#selectedLayerIds =
      pruned.length === this.#selectedLayerIds.size ? this.#selectedLayerIds : new Set(pruned);
  }

  /* ---------------- 选区 ---------------- */

  get selection(): SelectionMask | null {
    return this.#selection;
  }

  /** 设置当前选区。传 null 或空蒙版等价于取消选择。 */
  setSelection(mask: SelectionMask | null): void {
    this.#selection = mask !== null && mask.isEmpty ? null : mask;
    this.#notify();
  }

  /* ---------------- 提示与恢复 ---------------- */

  get hint(): string {
    return this.#hint;
  }

  setHint(text: string): void {
    this.#hint = text;
    this.#notify();
  }

  get recoverable(): readonly RecoverableEntry[] {
    return this.#recoverable;
  }

  setRecoverable(entries: readonly RecoverableEntry[]): void {
    this.#recoverable = entries;
    this.#notify();
  }
}

/** 显示预览用：把树里某个调整图层的参数换成预览值（结构共享的浅拷贝）。 */
function overrideAdjustment(root: GroupLayer, id: LayerId, adjustment: Adjustment): GroupLayer {
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === id && child.kind === 'adjustment') return { ...child, adjustment };
      if (child.kind === 'group') return overrideAdjustment(child, id, adjustment);
      return child;
    }),
  };
}

/** 视觉最上层的 raster（children 末位在上，组内递归）。 */
function topmostRaster(root: GroupLayer): LayerId | null {
  for (let i = root.children.length - 1; i >= 0; i--) {
    const layer = root.children[i]!;
    if (layer.kind === 'raster') return layer.id;
    if (layer.kind === 'group') {
      const inner = topmostRaster(layer);
      if (inner !== null) return inner;
    }
  }
  return null;
}
